class CartPage extends HTMLElement {
  constructor() {
    super();

    this.sectionId = this.dataset.sectionId;
    this.quantityInputs = this.querySelectorAll("[name='updates[]']");
    this.freeShippingBar = this.querySelector("free-shipping-bar");
    this.shippingEstimate = this.querySelector(".shipping-estimate");
    this.giftWrap = this.querySelector(".gift-wrap");

    this.debouncedOnChange = theme.helpers.debounce((event) => {
      this.onChange(event);
    }, 300);

    this.boundShippingEstimate = this.initShippingEstimate.bind(this);
    this.boundGiftWrap = this.initGiftWrap.bind(this);

    this.initCartActions();
  }

  initCartActions() {
    this.quantityInputs = this.querySelectorAll("[name='updates[]']");
    this.shippingEstimate = this.querySelector(".shipping-estimate");
    this.giftWrap = this.querySelector(".gift-wrap");

    this.quantityInputs.forEach((input) => {
      input.removeEventListener("change", this.debouncedOnChange, false);
      input.addEventListener("change", this.debouncedOnChange, false);
    });

    this.addressCountry = this.shippingEstimate?.querySelector("#AddressCountry");
    this.addressProvince = this.shippingEstimate?.querySelector("#AddressProvince");
    this.addressZip = this.shippingEstimate?.querySelector("#AddressZip");
    this.shippingEstimateButton = this.shippingEstimate?.querySelector(".shipping-estimate__button");

    if (this.addressCountry && this.addressProvince) {
      if (!this.shippingEstimate.classList.contains("shipping-estimate--inited")) {
        new Shopify.CountryProvinceSelector("AddressCountry", "AddressProvince", {
          hideElement: "AddressProvinceContainer",
        });

        this.shippingEstimate.classList.add("shipping-estimate--inited");
      }

      if (this.shippingEstimateButton) {
        this.shippingEstimateButton.removeEventListener("click", this.boundShippingEstimate, false);
        this.shippingEstimateButton.addEventListener("click", this.boundShippingEstimate, false);
      }
    }

    if (this.giftWrap) {
      const giftWrapInput = this.giftWrap.querySelector("input[type='checkbox']");
      giftWrapInput?.removeEventListener("change", this.boundGiftWrap, false);
      giftWrapInput?.addEventListener("change", this.boundGiftWrap, false);
    }
  }

  initGiftWrap(event) {
    const target = event.currentTarget;

    if (target.checked) {
      this.addGiftWrapProduct(target);
    } else {
      this.updateQuantity(target.dataset.index, 0, target.dataset.value, target);
    }
  }

  addGiftWrapProduct(target) {
    const id = +target.dataset.variantId;

    const body = JSON.stringify({
      id,
      quantity: 1,
      sections_url: window.location.pathname,
      sections: this.getSectionsToRender().map((section) => section.name),
    });

    this.classList.add("start", "loading");

    fetch(`${window.routes.cartAddUrl}`, {
      ...theme.helpers.fetchConfig(),
      body,
    })
      .then((response) => response.text())
      .then((state) => {
        this.updateCart(id, state, target);
      })
      .catch((e) => {
        throw e;
      })
      .finally(() => {
        this.classList.remove("start", "loading");
      });
  }

  initShippingEstimate(event) {
    event.preventDefault();

    const target = event.currentTarget;
    target.classList.add("loading");

    const data = {
      country: this.addressCountry.value || "",
      province: this.addressProvince.value || "",
      zip: this.addressZip.value || "",
    };

    this.getShippingRates(data, target);
  }

  getShippingRates(data, button) {
    fetch(
      `${window.Shopify.routes.root}cart/shipping_rates.json?shipping_address%5Bzip%5D=${data.zip}&shipping_address%5Bcountry%5D=${data.country}&shipping_address%5Bprovince%5D=${data.province}`
    )
      .then((response) => response.text())
      .then((state) => {
        const message = this.querySelector(".shipping-response");
        const successBanner = message?.querySelector(".color-success");
        const errorBanner = message?.querySelector(".color-error");

        if (!message || !successBanner || !errorBanner) return;

        message.classList.remove("!hidden");
        successBanner.classList.add("!hidden");
        errorBanner.classList.add("!hidden");

        const { showDeliveryDays, deliveryDayOne, deliveryDaysOther } = message.dataset;
        const parsedState = JSON.parse(state);

        if (parsedState && parsedState.shipping_rates) {
          if (parsedState.shipping_rates.length > 0) {
            successBanner.classList.remove("!hidden");

            const contentBlock = successBanner.querySelector("span");
            const addressInfo = window.cartStrings.shippingRate.replace(
              "{{address}}",
              `${data.zip}, ${data.country} ${data.province}`
            );

            let content = `<p>${addressInfo}</p>`;

            parsedState.shipping_rates.forEach((rate) => {
              let daysShipping = "";

              if (rate.delivery_days.length > 0 && showDeliveryDays === "true") {
                let typeDay = deliveryDayOne;
                const day = rate.delivery_days[0];
                const dayAt = rate.delivery_days.at(-1);

                if (day > 1) typeDay = deliveryDaysOther;

                daysShipping =
                  day === dayAt
                    ? `(${day} ${typeDay})`
                    : `(${day} - ${dayAt} ${typeDay})`;
              }

              content += `<p>${rate.name}: ${Shopify.formatMoney(
                rate.price,
                window.settings.moneyFormat
              )} ${daysShipping}</p>`;
            });

            contentBlock.innerHTML = content;
          } else {
            errorBanner.classList.remove("!hidden");
            errorBanner.querySelector("span").innerHTML = window.cartStrings.noShipping;
          }
        } else {
          errorBanner.classList.remove("!hidden");

          let content = "";

          Object.entries(parsedState).forEach((error) => {
            content += `${error[1][0]}<br>`;
          });

          errorBanner.querySelector("span").innerHTML = content;
        }
      })
      .catch((error) => {
        throw error;
      })
      .finally(() => {
        button?.classList.remove("loading");
      });
  }

  onChange(event) {
    const target = event.target.closest("[name='updates[]']") || event.target;

    const line = target.dataset.index || target.getAttribute("data-index");
    const quantity = target.value;

    if (!line || isNaN(Number(line))) {
      console.warn("Cart line index missing:", target);
      return;
    }

    this.updateQuantity(line, quantity, target.dataset.value, target);
  }

  updateQuantity(id, quantity, currentQuantity, target) {
    quantity = quantity ? quantity : 0;

    if (!id || isNaN(Number(id))) {
      console.warn("Invalid cart line:", id);
      return;
    }

    const body = JSON.stringify({
      line: +id,
      quantity: +quantity,
      sections_url: window.location.pathname,
      sections: this.getSectionsToRender().map((section) => section.name),
    });

    this.classList.add("start", "loading");

    fetch(`${window.routes.cartChangeUrl}`, {
      ...theme.helpers.fetchConfig(),
      body,
    })
      .then((response) => response.text())
      .then((state) => {
        this.updateCart(id, state, target);
      })
      .catch((e) => {
        throw e;
      })
      .finally(() => {
        this.classList.remove("start", "loading");
      });
  }

  updateCart(id, state, target) {
    const parsedState = JSON.parse(state);

    if (parsedState.errors) {
      this.updateMessageErrors(id, parsedState.errors, target);

      if (target) {
        target.value = target.dataset.value;
      }

      return;
    }

    document.dispatchEvent(
      new CustomEvent("theme:cart:count:update", {
        detail: {
          count: parsedState.item_count,
        },
      })
    );

    this.updateHeaderCartCount(parsedState.item_count);

    this.getSectionsToRender().forEach((section) => {
      const html = new DOMParser().parseFromString(parsedState.sections[section.name], "text/html");
      const currentSection = document.getElementById(section.id);

      if (!currentSection) return;

      if (parsedState.item_count === 0) {
        const parsedSection = html.getElementById(section.id);

        if (parsedSection) {
          currentSection.innerHTML = parsedSection.innerHTML;
        }

        this.classList.add("is-empty");
      } else {
        const currentProducts = currentSection.querySelector(".main-cart__content");
        const currentTotals = currentSection.querySelector(".cart-info .totals");
        const currentDiscounts = currentSection.querySelector(".cart-page__discounts");

        const parsedProducts = html.querySelector(".main-cart__content");
        const parsedTotals = html.querySelector(".cart-info .totals");
        const parsedDiscounts = html.querySelector(".cart-page__discounts");

        if (currentProducts && parsedProducts) {
          currentProducts.innerHTML = parsedProducts.innerHTML;
        }

        if (currentTotals && parsedTotals) {
          currentTotals.innerHTML = parsedTotals.innerHTML;
        }

        if (currentDiscounts && parsedDiscounts) {
          currentDiscounts.innerHTML = parsedDiscounts.innerHTML;
        }

        this.classList.remove("is-empty");

        const focusableElement = currentProducts?.querySelector("a");
        focusableElement?.focus();
      }
    });

    this.updateSubtotalPrice(parsedState);

    this.freeShippingBar = this.querySelector("free-shipping-bar");

    if (this.freeShippingBar) {
      if (parsedState.items_subtotal_price) {
        this.freeShippingBar.init(parsedState.items_subtotal_price);
      } else {
        fetch(`${window.routes.cartUrl}.json`)
          .then((res) => res.json())
          .then((cart) => {
            this.freeShippingBar.init(cart.items_subtotal_price);
            this.updateSubtotalPrice(cart);
          })
          .catch((error) => {
            throw error;
          });
      }
    }

    this.initCartActions();

    document.dispatchEvent(new CustomEvent("hover-button:apply"));
  }

  updateHeaderCartCount(count) {
    document
      .querySelectorAll(".cart-count, .cart-count-bubble, [data-cart-count], .header__cart-count")
      .forEach((el) => {
        el.textContent = count;
        el.setAttribute("data-cart-count", count);

        if (count > 0) {
          el.classList.remove("hidden", "!hidden");
        } else {
          el.classList.add("hidden");
        }
      });
  }

  updateSubtotalPrice(cart) {
    const price = cart.total_price || cart.items_subtotal_price || 0;
    const formattedPrice = Shopify.formatMoney(price, window.settings.moneyFormat);

    document.querySelectorAll(".cart-info .h6.fs-22, .cart-info .fs-22").forEach((el) => {
      el.textContent = formattedPrice;
    });
  }

  getSectionsToRender() {
    return [
      {
        name: this.sectionId,
        id: "CartPage",
      },
    ];
  }

  updateMessageErrors(line, message, target) {
    if (!target) return;

    target.setCustomValidity(message);
    target.reportValidity();
  }
}

if (!window.customElements.get("cart-page")) {
  customElements.define("cart-page", CartPage);
}